import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  MapContainer,
  TileLayer,
  CircleMarker,
  Popup,
  useMap,
  Marker,
  useMapEvents,
} from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPinned } from "lucide-react";

function FitMapToMarkers({ boxes }) {
  const map = useMap();

  useEffect(() => {
    const points = boxes
      .map((box) => ({
        lat: Number(box.latitude),
        lng: Number(box.longitude),
      }))
      .filter(
        (p) =>
          Number.isFinite(p.lat) &&
          Number.isFinite(p.lng) &&
          p.lat >= -90 &&
          p.lat <= 90 &&
          p.lng >= -180 &&
          p.lng <= 180 &&
          (p.lat !== 0 || p.lng !== 0),
      );

    if (points.length === 0) return;

    if (points.length === 1) {
      map.setView([points[0].lat, points[0].lng], 16);
      return;
    }

    const bounds = L.latLngBounds(points.map((p) => [p.lat, p.lng]));

    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 17 });
  }, [boxes, map]);

  return null;
}

function MapSizeInvalidator() {
  const map = useMap();

  useEffect(() => {
    const timer = setTimeout(() => {
      map.invalidateSize();
    }, 200);

    return () => clearTimeout(timer);
  }, [map]);

  return null;
}

export default function NAPBoxesMap({ boxes = [] }) {
  const validBoxes = boxes.filter((box) => {
    const lat = Number(box.latitude);
    const lng = Number(box.longitude);

    return (
      box.latitude !== null &&
      box.latitude !== undefined &&
      box.latitude !== "" &&
      box.longitude !== null &&
      box.longitude !== undefined &&
      box.longitude !== "" &&
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180 &&
      (lat !== 0 || lng !== 0)
    );
  });

  const defaultCenter = [14.55, 121.08];

  // const napBoxIcon = L.divIcon({
  //   className: "nap-box-marker",
  //   html: `
  //   <div style="
  //     width: 42px;
  //     height: 42px;
  //     background: #2563eb;
  //     border: 3px solid white;
  //     border-radius: 10px;
  //     display: flex;
  //     align-items: center;
  //     justify-content: center;
  //     box-shadow: 0 3px 8px rgba(0,0,0,0.35);
  //     color: white;
  //     font-size: 23px;
  //   ">
  //     ▣
  //   </div>
  // `,
  //   iconSize: [42, 42],
  //   iconAnchor: [21, 21],
  //   popupAnchor: [0, -21],
  // });

  function CenterMapButton({ boxes = [] }) {
    const map = useMap();

    const handleCenter = () => {
      const validBoxes = boxes.filter(
        (box) =>
          box.latitude != null &&
          box.longitude != null &&
          Number.isFinite(Number(box.latitude)) &&
          Number.isFinite(Number(box.longitude)),
      );

      if (validBoxes.length === 0) {
        map.setView([14.5995, 120.9842], 12);
        return;
      }

      const bounds = L.latLngBounds(
        validBoxes.map((box) => [Number(box.latitude), Number(box.longitude)]),
      );

      map.fitBounds(bounds, {
        padding: [50, 50],
        maxZoom: 17,
      });
    };

    return (
      <Button
        type="button"
        onClick={handleCenter}
        title="Center all NAP Boxes"
        aria-label="Center all NAP Boxes"
        style={{
          position: "absolute",
          top: "12px",
          right: "12px",
          zIndex: 1000,
          padding: "10px 14px",
          border: "1px solid #d1d5db",
          borderRadius: "8px",
          background: "#ffffff",
          color: "#1f2937",
          fontWeight: 600,
          cursor: "pointer",
          boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
        }}
      >
        <MapPinned /> Center Map
      </Button>
    );
  }

  return (
    <div className="w-full overflow-hidden rounded-xl border border-slate-200">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-white p-4">
        <div>
          <h2 className="font-bold text-slate-900">NAP Box Network Map</h2>
          <p className="text-sm text-slate-500">
            {validBoxes.length} mapped out of {boxes.length} NAP Boxes
          </p>
        </div>
      </div>

      {/* <MapContainer
        center={defaultCenter}
        zoom={12}
        scrollWheelZoom
        className="w-full"
        style={{ height: "65vh", minHeight: "400px" }}
      > */}
      <MapContainer
        center={defaultCenter}
        zoom={16}
        minZoom={3}
        maxZoom={19}
        scrollWheelZoom={true}
        style={{
          height: "600px",
          width: "100%",
          minHeight: "400px",
          background: "#e5e7eb",
        }}
      >
        <MapSizeInvalidator />
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxNativeZoom={19}
          maxZoom={22}
        />

        <FitMapToMarkers boxes={validBoxes} />

        {validBoxes.map((box) => {
          const color =
            box.status === "Active"
              ? "#16a34a"
              : box.status === "Maintenance"
                ? "#d97706"
                : "#64748b";

          return (
            // <Marker
            //   position={[Number(box.latitude), Number(box.longitude)]}
            //   icon={napBoxIcon}
            // >
            //   <Popup>
            //     <strong>{box.name}</strong>
            //     <br />
            //     Location: {box.location || "N/A"}
            //     <br />
            //     Ports: {box.occupied_ports || 0}/{box.total_ports || 0}
            //   </Popup>
            // </Marker>
            <CircleMarker
              key={box.id}
              center={[Number(box.latitude), Number(box.longitude)]}
              radius={8}
              pathOptions={{
                color: "#ffffff",
                weight: 2,
                fillColor: color,
                fillOpacity: 0.95,
              }}
            >
              <Popup>
                <div className="min-w-[180px] space-y-1">
                  <strong>{box.name}</strong>
                  <p>{box.location || "No address provided"}</p>
                  <p>Status: {box.status || "Unknown"}</p>
                  <p>Available: {box.available_ports ?? 0}</p>
                  <p>Occupied: {box.occupied_ports ?? 0}</p>
                  <p>Total ports: {box.total_ports ?? 8}</p>
                  {box.description && <p>{box.description}</p>}
                </div>
              </Popup>
            </CircleMarker>
          );
        })}

        <CenterMapButton boxes={boxes} />
      </MapContainer>

      <div className="flex flex-wrap gap-4 border-t bg-white p-3 text-sm">
        <span className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-green-600" />
          Active
        </span>
        <span className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-amber-600" />
          Maintenance
        </span>
        <span className="flex items-center gap-2">
          <span className="h-3 w-3 rounded-full bg-slate-500" />
          Inactive / Other
        </span>
      </div>

      {validBoxes.length === 0 && (
        <p className="bg-amber-50 p-3 text-sm text-amber-800">
          Walang NAP Box na may valid GPS coordinates. I-check ang latitude at
          longitude ng mga box.
        </p>
      )}
    </div>
  );
}
